import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import { MdAdd, MdDelete, MdArrowBack, MdSave } from 'react-icons/md';
import Switch from 'react-switch'
import { FaPrayingHands } from 'react-icons/fa';
import {
  get_prayer_list_by_id,
  create_new_prayer_list,
  update_prayer_list_by_id,
  delete_prayer_list_by_id
} from '../../api/prayerAPI';
import notify from '../../utils/IANotification';
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

export default function PrayerFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  let prayer_list_id = new URLSearchParams(window.location.search).get("prayer_list_id")

  const [activeTab, setActiveTab] = useState("general")
  const [prayerListData, setPrayerListData] = useState({
    type: "corporate",
    title: "",
    description: "",
    is_archived: "0",
  })
  const [prayers, setPrayers] = useState([])
  const [loading, setLoading] = useState(true)

  const tabs = [
    { id: 'general', label: 'Prayer List Info' },
    { id: 'prayers', label: 'Prayer Items' },
  ]

  useEffect(() => {
    fetchPrayerListById()
  }, [])

  const fetchPrayerListById = async () => {
    if (prayer_list_id === "new" || !prayer_list_id) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const data = await get_prayer_list_by_id(prayer_list_id);
      if (data?.status === 200) {
        setPrayerListData(data.data?.prayer_list || {});
        setPrayers(data.data?.prayers || []);
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false);
  }

  const savePrayerList = async () => {
    setLoading(true);
    try {
      const data = await create_new_prayer_list({
        ...prayerListData,
        prayers
      });
      if (data?.status === 200) {
        setPrayerListData(data.data);
        notify({ type: "success", message: "Saved prayer list" });
        navigate("/admin/prayer/details?prayer_list_id=" + data.data.id);
      } else {
        notify({ type: "error", message: "Failed to save prayer list" });
      }
    } catch (err) {
      notify({ type: "error", message: "Failed to save prayer list" });
    }
    setLoading(false);
  }

  const updatePrayerList = async () => {
    setLoading(true);
    if (prayer_list_id === "new" || !prayerListData.id) {
      savePrayerList();
      return;
    }
    try {
      const data = await update_prayer_list_by_id(prayer_list_id, {
        ...prayerListData,
        prayers
      });
      if (data?.status === 200) {
        fetchPrayerListById();
        notify({ type: "success", message: "Updated prayer list" });
      } else {
        notify({ type: "error", message: "Failed to update prayer list" });
      }
    } catch (err) {
      notify({ type: "error", message: "Failed to update prayer list" });
    }
    setLoading(false);
  }

  const deletePrayerList = async () => {
    setLoading(true);
    if (prayer_list_id === "new" || !prayer_list_id) return;
    try {
      const data = await delete_prayer_list_by_id(prayer_list_id);
      if (data?.status === 200) {
        notify({ type: "success", message: "Deleted prayer list" });
        navigate("/admin/prayer");
      } else {
        notify({ type: "error", message: "Failed to delete prayer list" });
      }
    } catch (err) {
      notify({ type: "error", message: "Failed to delete prayer list" });
    }
    setLoading(false);
  }

  return (
    <AdminLayout
      navSelected={"Corporate Prayer"}
      headerText={prayer_list_id === "new" ? "New Corporate Prayer List" : "Edit Prayer List"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/prayer')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {prayer_list_id === "new" ? "Create Prayer List" : prayerListData.title || "Edit Prayer List"}
            </h1>
          </div>
          <div className='flex gap-2'>
            {prayer_list_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deletePrayerList}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (prayer_list_id === "new") savePrayerList()
              else updatePrayerList()
            }}>
              {prayer_list_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: PRAYER LIST INFO */}
      {activeTab === "general" && (
        <Card title="Corporate Prayer List Details" subtitle="Prayer list title and overview summary.">
          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Prayer List Title</label>
              <input
                type="text"
                className="text-input"
                placeholder='e.g. BMC Church-Wide Intercession'
                value={prayerListData.title || ""}
                onChange={(e) => setPrayerListData({ ...prayerListData, title: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Overview Description</label>
              <textarea
                className="textarea-input h-32"
                value={prayerListData.description || ""}
                placeholder='e.g. Weekly intercessory prayer topics for the church community...'
                onChange={(e) => setPrayerListData({ ...prayerListData, description: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: PRAYER ITEMS */}
      {activeTab === "prayers" && (
        <Card
          title="Prayer Items &amp; Testimonies"
          subtitle="Specific prayer requests and praise reports."
          headerRight={
            <Button
              variant="secondary"
              size="sm"
              icon={MdAdd}
              onClick={() => setPrayers([...prayers, { content: "", is_archived: "0", is_answered: "0", user_id: "", prayer_list_id: "", can_repeat: "0", prayers_count: [], testimony: "" }])}
            >
              Add Prayer Point
            </Button>
          }
        >
          {prayers.length > 0 ? (
            <div className="space-y-4">
              {prayers.map((prayer, index) => (
                <div key={index} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-gray-900">Prayer Request #{index + 1}</span>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-gray-500 font-semibold">
                        <FaPrayingHands /> {prayer.prayers_count?.length || 0} intercessors
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        icon={MdDelete}
                        onClick={() => {
                          let newPrayers = [...prayers]
                          newPrayers.splice(index, 1)
                          setPrayers(newPrayers)
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  <div className="input-container">
                    <label className="input-label font-semibold">Prayer Request Content</label>
                    <textarea
                      className="textarea-input h-20"
                      value={prayer.content || ""}
                      onChange={(e) => {
                        let newPrayers = [...prayers]
                        newPrayers[index].content = e.target.value
                        setPrayers(newPrayers)
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-6 pt-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={prayer.is_answered == "1"}
                        onChange={(checked) => {
                          let newPrayers = [...prayers]
                          newPrayers[index].is_answered = checked ? "1" : "0"
                          setPrayers(newPrayers)
                        }}
                      />
                      <span className="text-xs font-semibold text-gray-700">Answered Prayer</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={prayer.can_repeat == "1"}
                        onChange={(checked) => {
                          let newPrayers = [...prayers]
                          newPrayers[index].can_repeat = checked ? "1" : "0"
                          setPrayers(newPrayers)
                        }}
                      />
                      <span className="text-xs font-semibold text-gray-700">Repeatable</span>
                    </div>
                  </div>

                  {prayer.is_answered == "1" && (
                    <div className="input-container">
                      <label className="input-label font-semibold">Praise Testimony</label>
                      <textarea
                        className="textarea-input h-20"
                        value={prayer.testimony || ""}
                        placeholder="Share testimony of how God answered this prayer..."
                        onChange={(e) => {
                          let newPrayers = [...prayers]
                          newPrayers[index].testimony = e.target.value
                          setPrayers(newPrayers)
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic py-6 text-center">No prayer requests added yet.</p>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
